import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { observer } from "mobx-react"
import { Button, Flex, Tag, theme, Typography } from "antd"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"

import rejectLottie from "@src/app/assets/reject.json"
import successLottie from "@src/app/assets/success.json"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { DefaultPageLayout } from "@src/app/layouts/DefaultPageLayout"
import { paths } from "@src/app/paths"
import uiStore from "@src/app/uiStore"
import { GlobalSwapsCollection } from "@src/pages/transactionsSwap/classes/ConvertsCollection"
import {
  ConvertResponse,
  ConvertResponseStatusEnum,
} from "@src/shared/api/generated"
import { TableExpandable } from "@src/shared/componemts/TableExpandable"
import { WithCopy } from "@src/shared/componemts/WithCopy"
import { dateTimeFormat } from "@src/shared/date"

export const SwapDetails = observer(() => {
  const collection = GlobalSwapsCollection()
  const { id } = useParams()
  const [data, setData] = useState<ConvertResponse | null>(null)
  const nav = useNavigate()
  const { isTransferioTheme } = uiStore()
  const currencies = GlobalCurrenciesCollection()

  const cMap = {
    [ConvertResponseStatusEnum.Failed]: "error",
    [ConvertResponseStatusEnum.Pending]: "geekblue",
    [ConvertResponseStatusEnum.Success]: "green",
  }

  const status = useMemo(() => {
    return data?.status
  }, [data])

  const [currencyFrom, currencyTo] = useMemo(() => {
    if (data) {
      const { network_from, network_to, symbol_from, symbol_to } = data

      const currencyFrom = currencies.getBySymbolNetwork({
        network: network_from as string,
        symbol: symbol_from as string,
      })

      const currencyTo = currencies.getBySymbolNetwork({
        network: network_to as string,
        symbol: symbol_to as string,
      })

      return [currencyFrom, currencyTo]
    }

    return [undefined, undefined]
  }, [data])

  useEffect(() => {
    if (id) {
      collection.getById(id).then((res) => {
        if (res) {
          setData(res)
        }
      })
    }
  }, [])

  const text = useMemo(() => {
    if (data) {
      const { amount_from, amount_to, symbol_from, symbol_to } = data

      if (status !== ConvertResponseStatusEnum.Failed) {
        return `You have successfully exchanged ${amount_from} ${currencyFrom?.symbol_alias ?? symbol_from} for ${amount_to} ${currencyTo?.symbol_alias ?? symbol_to}`
      }

      return "An error occurred. If this issue persists please contact us at:"
    }

    return ``
  }, [data, status, currencyFrom, currencyTo])

  const title = useMemo(() => {
    if (status !== ConvertResponseStatusEnum.Failed) {
      return "Successfully"
    }

    return "Reject"
  }, [status])

  const icon = useMemo(() => {
    if (status !== ConvertResponseStatusEnum.Failed) {
      return successLottie
    }

    return rejectLottie
  }, [status])

  const onBack = () => {
    nav(paths.swap)
  }

  return (
    <DefaultPageLayout hideBack isLoading={!data} title="Swap">
      <Flex align="center" gap={24} vertical>
        <Flex justify="center" style={{ width: "100%" }}>
          <DotLottieReact
            autoplay
            data={icon}
            height={120}
            style={{ height: 120, width: 120 }}
            width={120}
          />
        </Flex>

        <Flex align="center" gap={4} vertical>
          <Typography.Title level={4}>{title}</Typography.Title>

          <Typography.Text style={{ fontSize: 16 }}>{text}</Typography.Text>
        </Flex>

        <TableExpandable
          list={[
            ["ID:", data?.id ? <WithCopy content={data?.id} /> : "-"],
            ["Network from:", data?.network_from ?? "-"],
            [
              "Symbol from:",
              currencyFrom?.symbol_alias ?? data?.symbol_from ?? "-",
            ],
            [
              "Amount from:",
              data?.amount_from ? (
                <Typography.Text style={{ fontWeight: 600 }}>
                  {data.amount_from}
                </Typography.Text>
              ) : (
                "-"
              ),
            ],
            ["Network to:", data?.network_to ?? "-"],
            ["Symbol to:", currencyTo?.symbol_alias ?? data?.symbol_to ?? "-"],
            [
              "Amount to:",
              data?.amount_to ? (
                <Typography.Text style={{ fontWeight: 600 }}>
                  {data.amount_to}
                </Typography.Text>
              ) : (
                "-"
              ),
            ],
            [
              "Status",
              data?.status ? (
                <Tag
                  color={cMap[data.status ?? ConvertResponseStatusEnum.Pending]}
                >
                  {data.status}
                </Tag>
              ) : (
                "-"
              ),
            ],
            ["Rate:", data?.rate ?? "-"],
            [
              "Created at:",
              data?.created_at ? dateTimeFormat(data.created_at) : "-",
            ],
          ]}
          style={{ width: 600 }}
        />

        <Flex gap={16} vertical>
          <Button
            id="GENERATED_ID_CONVERTSUCCESS_BUTTON_BACK_TO_BALANCES_1"
            onClick={() => onBack()}
            size="large"
            type="primary"
          >
            Back to Swap
          </Button>

          {status === ConvertResponseStatusEnum.Failed && (
            <Typography.Link
              href={
                isTransferioTheme
                  ? "mailto:support@transfer.io"
                  : "mailto:support@cryptix.io"
              }
            >
              support@cryptix.io
            </Typography.Link>
          )}
        </Flex>
      </Flex>
    </DefaultPageLayout>
  )
})
